import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  addCustomChatParticipants,
  archiveCustomChatChannel,
  getCustomChatChannels,
  getCustomChatMessages,
  markCustomChatChannelRead,
  openCustomChatChannel,
  pickUpCustomChatChannel,
  removeCustomChatParticipant,
  sendCustomChatMessage,
  sendCustomChatGeneralMessage,
  setCustomChatExternalLock,
  transferCustomChatChannel,
} from '../api';
import type {
  CustomChatAddParticipantsPayload,
  CustomChatChannel,
  CustomChatMessage,
  CustomChatParticipantIdentity,
  CustomChatOpenChannelPayload,
  CustomChatSendGeneralMessagePayload,
  CustomChatSendMessagePayload,
  CustomChatTransferPayload,
} from '../types';
import { sortCustomChatChannelsByRecentActivity } from '../utils/channelOrdering';

export const CUSTOM_CHAT_CHANNELS_QUERY_KEY = ['customChat', 'channels'];
export const CUSTOM_CHAT_MESSAGES_PAGE_SIZE = 50;
export const CUSTOM_CHAT_CHANNELS_POLL_INTERVAL = 30000;
export const CUSTOM_CHAT_CHANNELS_ACTIVE_POLL_INTERVAL = 30000;
export const CUSTOM_CHAT_MESSAGES_POLL_INTERVAL = 10000;

export const customChatChannelQueryKey = (
  channelId?: number | string | null
) => ['customChat', 'channel', channelId];

export const customChatMessagesQueryKey = (
  channelId?: number | string | null
) => ['customChat', 'messages', channelId];

const mergeCustomChatMessages = (
  currentMessages: CustomChatMessage[] = [],
  newMessages: CustomChatMessage[] = [],
  placement: 'append' | 'prepend' = 'append'
) => {
  const existingMessageIds = new Set(
    currentMessages.map((message) => message.id)
  );
  const uniqueNewMessages = newMessages.filter(
    (message) => !existingMessageIds.has(message.id)
  );

  return placement === 'prepend'
    ? [...uniqueNewMessages, ...currentMessages]
    : [...currentMessages, ...uniqueNewMessages];
};

const getOldestMessage = (messages: CustomChatMessage[] = []) =>
  messages[0] ?? null;

const getNewestMessage = (messages: CustomChatMessage[] = []) =>
  messages[messages.length - 1] ?? null;

const upsertAndSortCustomChatChannels = (
  channels: CustomChatChannel[],
  channel: CustomChatChannel
) =>
  sortCustomChatChannelsByRecentActivity([
    channel,
    ...channels.filter(
      (previousChannel) => String(previousChannel.id) !== String(channel.id)
    ),
  ]);

const setCustomChatChannelCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  channel: CustomChatChannel
) => {
  queryClient.setQueryData<CustomChatChannel>(
    customChatChannelQueryKey(channel.id),
    channel
  );
  queryClient.setQueryData<CustomChatChannel[]>(
    CUSTOM_CHAT_CHANNELS_QUERY_KEY,
    (previousChannels = []) =>
      upsertAndSortCustomChatChannels(previousChannels, channel)
  );
};

const updateCustomChatChannelCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  channelId: number | string | null,
  updater: (channel: CustomChatChannel) => CustomChatChannel
) => {
  if (!channelId) return;

  queryClient.setQueryData<CustomChatChannel>(
    customChatChannelQueryKey(channelId),
    (previousChannel) =>
      previousChannel ? updater(previousChannel) : previousChannel
  );
  queryClient.setQueryData<CustomChatChannel[]>(
    CUSTOM_CHAT_CHANNELS_QUERY_KEY,
    (previousChannels) =>
      previousChannels
        ? sortCustomChatChannelsByRecentActivity(
            previousChannels.map((previousChannel) =>
              String(previousChannel.id) === String(channelId)
                ? updater(previousChannel)
                : previousChannel
            )
          )
        : previousChannels
  );
};

export const useCustomChatChannels = (
  enabled: boolean,
  options: { refetchInterval?: number | false } = {}
) =>
  useQuery({
    enabled,
    queryFn: async () =>
      sortCustomChatChannelsByRecentActivity(await getCustomChatChannels()),
    queryKey: CUSTOM_CHAT_CHANNELS_QUERY_KEY,
    keepPreviousData: true,
    refetchInterval:
      options.refetchInterval === false
        ? false
        : enabled
        ? options.refetchInterval ?? CUSTOM_CHAT_CHANNELS_POLL_INTERVAL
        : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    staleTime: 10000,
  });

export const useCustomChatMessages = (
  channelId: number | string | null,
  enabled: boolean
) => {
  const queryClient = useQueryClient();
  const [hasOlderMessages, setHasOlderMessages] = useState(true);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const hasOlderMessagesRef = useRef(true);
  const isLoadingOlderRef = useRef(false);
  const isPollingNewMessagesRef = useRef(false);

  const setHasOlderMessagesState = useCallback((value: boolean) => {
    hasOlderMessagesRef.current = value;
    setHasOlderMessages(value);
  }, []);

  const setIsLoadingOlderState = useCallback((value: boolean) => {
    isLoadingOlderRef.current = value;
    setIsLoadingOlder(value);
  }, []);

  const query = useQuery({
    enabled: Boolean(enabled && channelId),
    queryFn: () =>
      getCustomChatMessages(channelId as number | string, {
        limit: CUSTOM_CHAT_MESSAGES_PAGE_SIZE,
      }),
    queryKey: customChatMessagesQueryKey(channelId),
    onSuccess: (messages) => {
      setHasOlderMessagesState(
        messages.length >= CUSTOM_CHAT_MESSAGES_PAGE_SIZE
      );
    },
    refetchOnWindowFocus: false,
    staleTime: CUSTOM_CHAT_MESSAGES_POLL_INTERVAL,
  });

  useEffect(() => {
    setIsLoadingOlderState(false);
    setHasOlderMessagesState(Boolean(enabled && channelId));
  }, [channelId, enabled, setHasOlderMessagesState, setIsLoadingOlderState]);

  const loadOlderMessages = useCallback(async () => {
    if (
      !enabled ||
      !channelId ||
      isLoadingOlderRef.current ||
      !hasOlderMessagesRef.current
    ) {
      return;
    }

    const queryKey = customChatMessagesQueryKey(channelId);
    const currentMessages =
      queryClient.getQueryData<CustomChatMessage[]>(queryKey) ?? [];
    const oldestMessage = getOldestMessage(currentMessages);

    if (!oldestMessage) {
      setHasOlderMessagesState(false);
      return;
    }

    setIsLoadingOlderState(true);

    try {
      const olderMessages = await getCustomChatMessages(channelId, {
        before: String(oldestMessage.id),
        limit: CUSTOM_CHAT_MESSAGES_PAGE_SIZE,
      });

      queryClient.setQueryData<CustomChatMessage[]>(
        queryKey,
        (previousMessages) =>
          mergeCustomChatMessages(previousMessages, olderMessages, 'prepend')
      );

      if (olderMessages.length < CUSTOM_CHAT_MESSAGES_PAGE_SIZE) {
        setHasOlderMessagesState(false);
      }
    } catch (error) {
      console.error('Unable to fetch older custom chat messages', error);
    } finally {
      setIsLoadingOlderState(false);
    }
  }, [
    channelId,
    enabled,
    queryClient,
    setHasOlderMessagesState,
    setIsLoadingOlderState,
  ]);

  useEffect(() => {
    if (!enabled || !channelId) return undefined;

    const intervalId = window.setInterval(() => {
      if (isPollingNewMessagesRef.current) return;

      const queryKey = customChatMessagesQueryKey(channelId);
      const currentMessages =
        queryClient.getQueryData<CustomChatMessage[]>(queryKey) ?? [];
      const newestMessage = getNewestMessage(currentMessages);
      const params = newestMessage
        ? {
            after: String(newestMessage.id),
            limit: CUSTOM_CHAT_MESSAGES_PAGE_SIZE,
          }
        : { limit: CUSTOM_CHAT_MESSAGES_PAGE_SIZE };

      isPollingNewMessagesRef.current = true;

      void getCustomChatMessages(channelId, params)
        .then((newMessages) => {
          if (!newMessages.length) return;

          queryClient.setQueryData<CustomChatMessage[]>(
            queryKey,
            (previousMessages) =>
              mergeCustomChatMessages(previousMessages, newMessages, 'append')
          );
          const newestMessage = getNewestMessage(newMessages);

          if (newestMessage) {
            updateCustomChatChannelCache(queryClient, channelId, (channel) => ({
              ...channel,
              last_message: newestMessage,
              last_message_at: newestMessage.created_at,
            }));
          }
        })
        .catch((error) => {
          console.error('Unable to fetch new custom chat messages', error);
        })
        .finally(() => {
          isPollingNewMessagesRef.current = false;
        });
    }, CUSTOM_CHAT_MESSAGES_POLL_INTERVAL);

    return () => window.clearInterval(intervalId);
  }, [channelId, enabled, queryClient]);

  return {
    ...query,
    hasOlderMessages,
    isLoadingOlder,
    loadOlderMessages,
  };
};

export const useOpenCustomChatChannelMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CustomChatOpenChannelPayload) =>
      openCustomChatChannel(payload),
    onSuccess: (channel) => {
      setCustomChatChannelCache(queryClient, channel);
    },
  });
};

export const usePickUpCustomChatChannelMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (channelId: number | string) =>
      pickUpCustomChatChannel(channelId),
    onSuccess: (channel) => {
      setCustomChatChannelCache(queryClient, channel);
    },
  });
};

export const useTransferCustomChatChannelMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      channelId,
      payload,
    }: {
      channelId: number | string;
      payload: CustomChatTransferPayload;
    }) => transferCustomChatChannel(channelId, payload),
    onSuccess: (channel) => {
      setCustomChatChannelCache(queryClient, channel);
    },
  });
};

export const useSendCustomChatMessageMutation = (
  channelId: number | string | null
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CustomChatSendMessagePayload) =>
      sendCustomChatMessage(channelId as number | string, payload),
    onSuccess: (message) => {
      queryClient.setQueryData<CustomChatMessage[]>(
        customChatMessagesQueryKey(channelId),
        (previousMessages) =>
          mergeCustomChatMessages(previousMessages, [message], 'append')
      );
      updateCustomChatChannelCache(queryClient, channelId, (channel) => ({
        ...channel,
        last_message: message,
        last_message_at: message.created_at,
      }));
    },
  });
};

export const useSendCustomChatGeneralMessageMutation = (
  channelId: number | string | null
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CustomChatSendGeneralMessagePayload) =>
      sendCustomChatGeneralMessage(channelId as number | string, payload),
    onSuccess: (message) => {
      queryClient.setQueryData<CustomChatMessage[]>(
        customChatMessagesQueryKey(channelId),
        (previousMessages) =>
          mergeCustomChatMessages(previousMessages, [message], 'append')
      );
      updateCustomChatChannelCache(queryClient, channelId, (channel) => ({
        ...channel,
        last_message: message,
        last_message_at: message.created_at,
      }));
    },
  });
};

export const useMarkCustomChatReadMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (channelId: number | string) =>
      markCustomChatChannelRead(channelId),
    onSuccess: (result, channelId) => {
      updateCustomChatChannelCache(queryClient, channelId, (channel) => ({
        ...channel,
        unread_count: result.unread_count ?? 0,
      }));
    },
  });
};

export const useArchiveCustomChatChannelMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      archived,
      channelId,
    }: {
      archived?: boolean;
      channelId: number | string;
    }) => archiveCustomChatChannel(channelId, archived ?? true),
    onSuccess: (channel) => {
      setCustomChatChannelCache(queryClient, channel);
    },
  });
};

export const useSetCustomChatExternalLockMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      channelId,
      locked,
    }: {
      channelId: number | string;
      locked: boolean;
    }) => setCustomChatExternalLock(channelId, locked),
    onSuccess: (channel, variables) => {
      setCustomChatChannelCache(queryClient, channel);
      queryClient.invalidateQueries(
        customChatMessagesQueryKey(variables.channelId)
      );
    },
  });
};

export const useAddCustomChatParticipantsMutation = (
  channelId: number | string | null
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CustomChatAddParticipantsPayload) =>
      addCustomChatParticipants(channelId as number | string, payload),
    onSuccess: (channel) => {
      setCustomChatChannelCache(queryClient, channel);
      queryClient.invalidateQueries(customChatMessagesQueryKey(channelId));
    },
  });
};

export const useRemoveCustomChatParticipantMutation = (
  channelId: number | string | null
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (participant: CustomChatParticipantIdentity) =>
      removeCustomChatParticipant(channelId as number | string, participant),
    onSuccess: (channel) => {
      setCustomChatChannelCache(queryClient, channel);
      queryClient.invalidateQueries(customChatMessagesQueryKey(channelId));
    },
  });
};
